<template>
  <div
    class="flex justify-start text-gray4 md:mb-0"
    data-testid="filter-control-section"
    :class="[
      singleRowScroll ? 'scrollbar-hide mb-0 flex-nowrap overflow-x-auto whitespace-nowrap md:flex-wrap md:overflow-visible' : 'mb-4 flex-wrap',
      // full-bleed スクロール: スクロール領域は全幅のまま、中身のチップに左右余白を付ける（端までスクロール可・ビタ付け防止）
      fullBleedScroll ? 'px-4 md:px-0' : '',
    ]"
  >
    <button
      aria-label="filter button"
      class="flex h-[40px] min-w-[140px] max-w-[240px] shrink-0 items-center rounded-sm border-[0.5px] border-beige3 px-4 hover:bg-gray5"
      data-testid="filter-button"
      :class="{ 'is-filtered': hasQuery }"
      @click="handleOpenSearchExperiencesModal"
    >
      <img alt="フィルターボタン" class="mr-2" :src="filterIconSrc" />
      <span class="text-inherit">Add Filters</span>
    </button>
    <button
      aria-label="select dates button"
      class="ml-2 flex h-[40px] min-w-[120px] max-w-[240px] shrink-0 items-center rounded-sm border-[0.5px] border-beige3 px-4 hover:bg-gray5 md:ml-4"
      data-testid="calendar-select-button"
      :class="{ 'is-filtered': hasExperienceStartDatesQuery }"
      @click="handleOpenSelectDatesModal"
    >
      <img alt="カレンダー選択ボタン" class="mr-2" :src="calendarIconSrc" />
      <span class="overflow-hidden text-ellipsis whitespace-nowrap text-inherit">{{ datesButtonLabel }}</span>
    </button>
    <!-- モバイルで改行し、即時予約ボタンは次の行で自然幅にする（1行スクロール時は改行しない） -->
    <span v-if="!singleRowScroll" aria-hidden="true" class="h-0 basis-full md:hidden"></span>
    <button
      aria-label="instant booking button"
      class="flex h-[40px] min-w-[120px] max-w-[240px] shrink-0 items-center rounded-sm border-[0.5px] border-beige3 py-2 hover:bg-gray5 md:ml-4 md:mt-0"
      data-testid="instant-booking-button"
      :class="[
        singleRowScroll ? 'ml-2 mt-0 md:ml-4' : 'mt-2',
        {
          'is-filtered': isInstantBookingSelected,
          'flex-row-reverse': isInstantBookingSelected,
          'pl-4 pr-3': isInstantBookingSelected,
          'pl-3 pr-4': !isInstantBookingSelected,
        },
      ]"
      @click="handleClickInstantBooking"
    >
      <img class="flex-shrink-0" :alt="isInstantBookingSelected ? '即時予約解除' : '即時予約選択ボタン'" :class="isInstantBookingSelected ? 'ml-2' : 'mr-2'" :src="instantBookingIconSrc" />
      <span class="overflow-hidden text-ellipsis whitespace-nowrap text-inherit">Instant Booking</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import { useAsset } from '@/composables/utils/useAsset';
import { formatDateLabel } from '@/wabunka-src/common/features/Experience/components/FilterControlSection/formatDateLabel';
import { sendGAWabunkaClickAddFiltersEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickAddFilters';
import { sendGAWabunkaClickInstantBookingEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickInstantBooking';
import { sendGAWabunkaClickSelectDatesEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickSelectDates';

import type { Dayjs } from 'dayjs';

interface Props {
  destinationTagsQuery: string;
  themeTagsQuery: string;
  experienceEventStartAtDatesQuery: string;
  selectedDates?: Dayjs[];
  /** カンマ区切り preference webPath（例: instant-booking）。選択状態の同期に使用 */
  preferenceTagsQuery?: string;
  /** Instant Booking ボタン押下時のコールバック */
  onInstantBookingClick?: () => void;
  /** モバイルで折返さず1行横スクロールにする（マップビュー用。既定は従来の2行折返し） */
  singleRowScroll?: boolean;
  /** 全幅スクロール（画面端まで）にしつつ、中身のチップに左右余白を付ける。SP マップ全画面のフルブリード用。 */
  fullBleedScroll?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  preferenceTagsQuery: '',
  singleRowScroll: false,
  fullBleedScroll: false,
});

const hasQuery = computed(() => {
  return !!(props.destinationTagsQuery || props.themeTagsQuery || props.experienceEventStartAtDatesQuery);
});
const hasExperienceStartDatesQuery = computed(() => {
  return !!props.experienceEventStartAtDatesQuery;
});
const datesButtonLabel = computed<string>(() => {
  const dateLabel = formatDateLabel(props.selectedDates || []);
  return dateLabel || 'Select Dates';
});

const isInstantBookingSelected = computed(() => {
  return props.preferenceTagsQuery
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .includes('instant-booking');
});

const { getAssetPath } = useAsset();
const filterIconSrc = computed(() => getAssetPath(hasQuery.value ? 'filter/gold.svg' : 'filter/default.svg'));
const calendarIconSrc = computed(() => getAssetPath(hasExperienceStartDatesQuery.value ? 'calendar_add_on/gold.svg' : 'calendar_add_on/default.svg'));
const instantBookingIconSrc = computed(() => getAssetPath(isInstantBookingSelected.value ? 'close/gold.svg' : 'add/gray.svg'));

const { open } = useGlobalModal('searchExperiencesModal');
const handleOpenSearchExperiencesModal = () => {
  sendGAWabunkaClickAddFiltersEvent();
  open();
};

const { updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');
const handleOpenSelectDatesModal = () => {
  sendGAWabunkaClickSelectDatesEvent();
  // カレンダー選択タブを表示する
  updateTabIndex(1);
  open();
};

const handleClickInstantBooking = () => {
  sendGAWabunkaClickInstantBookingEvent();
  props.onInstantBookingClick?.();
};
</script>

<style scoped>
.is-filtered {
  border-color: #826b3d;
  color: #826b3d;
  background-color: #f8f6f1;
}

/* single-row-scroll（モバイル1行横スクロール）時にスクロールバーを非表示にする（スクロール機能は維持） */
.scrollbar-hide {
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
